import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import SecurityBanner from './components/SecurityBanner';
import HomePage from './pages/HomePage';
import BatchResize from './pages/BatchResize';
import ImageCompress from './pages/ImageCompress';
import BatchCompress from './pages/BatchCompress';
import Features from './pages/Features';
import HowItWorks from './pages/HowItWorks';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminPanel from './pages/AdminPanel';
import './App.css';

function App() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize i18n and app
    const initializeApp = async () => {
      try {
        // Wait for i18n to be ready
        await i18n.isInitialized;
        
        // Set HTML lang attribute
        document.documentElement.lang = i18n.language;
        
        const savedConsent = localStorage.getItem('cookieConsent');
        if (savedConsent) {
          const consent = JSON.parse(savedConsent);
          setCookieConsent(consent);
          initializeServices(consent);
        } else {
          setShowCookieBanner(true);
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        setShowCookieBanner(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      document.documentElement.lang = lng;
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, []);

  const initializeServices = (consent) => {
    try {
      // Initialize Google Analytics
      if (consent.analytics && typeof window.gtag !== 'undefined') {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      } else if (typeof window.gtag !== 'undefined') {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }

      // Initialize Google AdSense
      if (consent.advertising) {
        // Enable personalized ads
        if (typeof window.gtag !== 'undefined') {
          window.gtag('consent', 'update', {
            'ad_storage': 'granted',
            'ad_user_data': 'granted',
            'ad_personalization': 'granted'
          });
        }
        
        // Initialize AdSense with personalized ads
        if (window.adsbygoogle && !window.adsbygoogle.loaded) {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({
              google_ad_client: "ca-pub-2861454200023917",
              enable_page_level_ads: true,
              overlays: {bottom: true}
            });
            window.adsbygoogle.loaded = true;
          } catch (e) {
            console.log('AdSense initialization error:', e);
          }
        }
      } else {
        // Disable personalized ads - show generic ads
        if (typeof window.gtag !== 'undefined') {
          window.gtag('consent', 'update', {
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied'
          });
        }
        
        // Initialize AdSense with non-personalized ads
        if (window.adsbygoogle && !window.adsbygoogle.loaded) {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({
              google_ad_client: "ca-pub-2861454200023917",
              enable_page_level_ads: true,
              overlays: {bottom: true},
              tag_for_child_directed_treatment: 1 // Ensures non-personalized ads
            });
            window.adsbygoogle.loaded = true;
          } catch (e) {
            console.log('AdSense initialization error:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error initializing services:', error);
    }
  };

  const handleCookieConsent = (consent) => {
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(consent));
      setCookieConsent(consent);
      setShowCookieBanner(false);
      
      // Initialize services based on consent
      initializeServices(consent);
      
      console.log('Cookie consent updated:', consent);
    } catch (error) {
      console.error('Error handling cookie consent:', error);
    }
  };

  // Show loading state to prevent white page
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        <Router>
          <div className="App min-h-screen flex flex-col">
            <Header />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/batch-resize" element={<BatchResize />} />
                <Route path="/compress" element={<ImageCompress />} />
                <Route path="/batch-compress" element={<BatchCompress />} />
                <Route path="/features" element={<Features />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/admin" element={<AdminPanel />} />
                
                {/* Language-specific routes */}
                <Route path="/:lang" element={<HomePage />} />
                <Route path="/:lang/batch-resize" element={<BatchResize />} />
                <Route path="/:lang/compress" element={<ImageCompress />} />
                <Route path="/:lang/batch-compress" element={<BatchCompress />} />
                <Route path="/:lang/features" element={<Features />} />
                <Route path="/:lang/how-it-works" element={<HowItWorks />} />
                <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
                <Route path="/:lang/admin" element={<AdminPanel />} />
              </Routes>
            </main>
            <Footer />
            
            {showCookieBanner && <CookieBanner onConsent={handleCookieConsent} />}
            <SecurityBanner />
          </div>
        </Router>
      </HelmetProvider>
    </I18nextProvider>
  );
}

export default App;